import React, { useState, useEffect, useContext, memo } from "react";
import { Link } from "react-router-dom";
import { Skeleton } from '@material-ui/lab';
import { Divider, Backdrop, CircularProgress, Snackbar } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import MuiAlert from '@material-ui/lab/Alert';
import { HttpService } from "../../apiAuthorization/httpService";
import { OfflineService } from "../../offline/storageService/offlineService";
import { GlobalTheme, TileTheme} from "../../layouts/theme";
import { Theme } from "../contexts/themeContext.js";
import { Context } from "../contexts/siteContext.js";
import { Modules } from '../contexts/modulesContext';
import Card from "../card/card.js";
import CardHeader from "../card/cardHeader.js";
import CardBody from "../card/cardBody.js";
import CardContent from '@material-ui/core/CardContent';

function Alert(props) {
    return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const useStyles = makeStyles(theme => ({
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: "#fff",
        position: "absolute"
    }
}));

const PatientSittingChart = (props) => {
    const classes = useStyles();
    const tileClasses = TileTheme();
    const offlineService = new OfflineService();
    const { defaultSite, siteLoading } = useContext(Context);
    const { theme } = useContext(Theme);
    const { trainingMode } = useContext(Modules);
    const [loading, setLoading] = useState(true);
    const [backDrop, setBackDrop] = useState(false);
    const [totalCloseObservation, setTotalCloseObservation] = useState(0);
    const [total1To1, setTotal1To1] = useState(0);
    const [totalSafety, setTotalSafety] = useState(0);
    const [totalSuicide, setTotalSuicide] = useState(0);
    const [totalEscalations, setTotalEscalations] = useState(0);
    const [totalHours, setTotalHours] = useState(0);
    const [isManager, setIsManager] = useState(false);
    const [openError, setOpenError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const currentTheme = GlobalTheme[theme];

    const closeErrorSnackbar = () => {
        setOpenError(false);
    };

    const linkClicked = (e) => {
       if (sessionStorage.getItem("isOffline") === "1") {
            e.preventDefault();
            setErrorMessage("Patient Observation cannot be accessible in offline mode. Please connect to the internet.");
            setOpenError(true);
        }
    }

    const getPatientSittings = () => {
        setBackDrop(true);
        HttpService.post("/PatientSitting/GetPatientSittingSummary", { Date: new Date(), SiteId: defaultSite.id, TrainingMode: trainingMode })
            .then(async (result) => {
                if (result && result.offline) {
                    let offlineResult = await offlineService.getOfflinesBySite(defaultSite.id, "PatientSittingSummary");
                    result = offlineResult.OfflineData;
                }
                else {
                    offlineService.removeOfflineBySite(defaultSite.id, "PatientSittingSummary");
                    offlineService.addOffline(defaultSite.id, "PatientSittingSummary", result);
                }

                if (result.data != null) {
                    setTotalCloseObservation(result.data.TotalCloseObservation);
                    setTotal1To1(result.data.Total1To1);
                    setTotalSafety(result.data.TotalSafety);
                    setTotalSuicide(result.data.TotalSuicide);
                    setTotalEscalations(result.data.TotalEscalations);
                    setTotalHours(result.data.TotalHours);
                    setLoading(false);
                    setBackDrop(false);
                }
            },
                (error) => {
                    console.log(error);
                    setBackDrop(false);
                });
    };

    const switchUrlPath = () => {
        if (isManager) return { pathname: "/ObservationDashboard" };
        return { pathname: "/PatientSittingList" }
    };

    const getRole = () => {
        HttpService.post('/PatientSitting/GetRole',{siteId: defaultSite.id })
        .then((result) => {
            setIsManager(result.manager);
        },
        (error) => {
            console.log(error);
        });
    };

    useEffect(() => {
        getRole();
        // eslint-disable-next-line 
    }, []);

    useEffect(() => {
        if (!siteLoading)
            getPatientSittings();
        // eslint-disable-next-line 
    }, [props.mode, defaultSite, siteLoading]);

    return (<>
        <div style={{ position: "relative" }}>
            <Snackbar open={openError} autoHideDuration={3000} onClose={closeErrorSnackbar} style={{ height: "100%" }}>
                <Alert onClose={closeErrorSnackbar} severity="error">
                    {errorMessage}
                </Alert>
            </Snackbar>
            <Link to={switchUrlPath} onClick={(e) => linkClicked(e)}>
                <Backdrop className={classes.backdrop} id="patientSittingBackdrop" open={backDrop}>
                    <CircularProgress color="inherit" />
                </Backdrop>
                <Card chart color="primary" className={tileClasses.card} style={{ background: currentTheme.background, color: currentTheme.text }} >
                    <CardContent className={tileClasses.cardContent}>
                        <CardHeader>
                            <div className={tileClasses.title}>Patient Observation</div>
                            {loading === true ? (<Skeleton variant="rect" height={76} />) : null}
                        </CardHeader>
                        <Divider />
                        <CardBody>
                            <div className={tileClasses.subTitle}>Active Observations</div>
                            {loading === true ? (<div><Skeleton variant="text" /><Skeleton variant="text" /></div>) : (
                                <div className="d-flex flex-column">
                                    {(defaultSite.IntegratedSystem.POSGroupId === 2) &&
                                    <>
                                        <label className={tileClasses.optionText}>Close Observation: {totalCloseObservation}</label>
                                        <label className={tileClasses.optionText}>1:1: {total1To1}</label>
                                    </>
                                    }
                                    {(defaultSite.IntegratedSystem.POSGroupId === 1) &&
                                        <label className={tileClasses.optionText}>Lower Risk: {totalSafety}</label>
                                    }
                                    <label className={tileClasses.optionText}>Total Hours: {totalHours}</label>
                                    {(defaultSite.IntegratedSystem.POSGroupId === 1) &&
                                        <label className={tileClasses.optionText}>High Risk: {totalSuicide}</label>
                                    }
                                    <label className={tileClasses.optionText}>Escalations: {totalEscalations}</label>
                                </div>
                            )}
                        </CardBody>
                    </CardContent>
                </Card>
            </Link>
        </div>
    </>
    );
}

export default memo(PatientSittingChart);
